import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import Spinner from "@/components/ui/Spinner";
import * as XLSX from 'xlsx';
import { db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { COLLECTION_CONFIGS } from '@/utils/dataConfigs';
import { useAuth } from '@/contexts/AuthContext';

export default function Import({
    isOpen,
    onClose,
    targetCollection = 'deal_audits',
    companyId,
    onComplete
}) {
    const { user } = useAuth();
    const config = COLLECTION_CONFIGS[targetCollection] || COLLECTION_CONFIGS.deal_audits;
    
    // HubSpot 4-step Architecture: 1=Type, 2=Upload, 3=Map, 4=Details
    const [step, setStep] = useState(1);
    
    // State
    const [importSource, setImportSource] = useState(''); 
    const [fileName, setFileName] = useState('');
    const [rawData, setRawData] = useState([]);
    const [sourceHeaders, setSourceHeaders] = useState([]);
    const [fieldMapping, setFieldMapping] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    
    const fileInputRef = useRef(null);

    const steps = [
        { id: 1, label: 'Type' },
        { id: 2, label: 'Upload' },
        { id: 3, label: 'Map' },
        { id: 4, label: 'Details' }
    ];

    const matchFields = (headers) => {
        const initialMapping = {};
        config.fields.forEach(f => {
            const match = headers.find(h =>
                h.toLowerCase().includes(f.label.toLowerCase()) ||
                h.toLowerCase().includes(f.key.toLowerCase()) ||
                (f.key === 'amount' && h.toLowerCase().includes('value')) ||
                (f.key === 'companyName' && h.toLowerCase().includes('account'))
            );
            if (match) initialMapping[f.key] = match;
        });
        setFieldMapping(initialMapping);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setIsProcessing(true);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const workbook = XLSX.read(bstr, { type: 'binary' });
                const wsname = workbook.SheetNames[0];
                const ws = workbook.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { defval: "" }); // defval to include empty columns

                if (data.length > 0) {
                    const headers = Object.keys(data[0]);
                    setSourceHeaders(headers);
                    setRawData(data);
                    matchFields(headers);
                    setStep(3); // Auto-advance to Map
                } else {
                    alert("The file appears to be empty.");
                }
            } catch {
                alert("Failed to parse the file. Please ensure it is a valid CSV or XLSX.");
            } finally {
                setIsProcessing(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleImportExecution = async () => {
        if (!companyId) return;
        setIsProcessing(true);

        try {
            const batch = [];
            let importedCount = 0;

            for (const row of rawData) {
                const mappedRow = {
                    createdAt: serverTimestamp(),
                    createdBy: user?.email || 'System Import',
                    status: 'Pending Audit',
                    isImported: true
                };

                config.fields.forEach(f => {
                    const sourceKey = fieldMapping[f.key];
                    if (sourceKey && row[sourceKey] !== undefined && row[sourceKey] !== "") {
                        let val = row[sourceKey];
                        if (f.key === 'amount' || f.key === 'value') val = parseFloat(val) || 0;
                        mappedRow[f.key] = val;
                    }
                });

                batch.push(addDoc(collection(db, 'companies', companyId, targetCollection), mappedRow));
                importedCount++;
            }

            await Promise.all(batch);
            
            // Log to Data Transfers Audit Log
            try {
                await addDoc(collection(db, 'companies', companyId, 'data_transfers'), {
                    type: 'IMPORT',
                    module: config.label,
                    fileName: fileName || importSource,
                    records: importedCount,
                    status: 'Completed',
                    createdAt: serverTimestamp(),
                    createdBy: user?.email || 'System User'
                });
            } catch {
                // Ignore
            }

            if (onComplete) onComplete();
            
            // Success State
            setTimeout(() => {
                onClose();
                // Reset state
                setTimeout(() => {
                    setStep(1);
                    setRawData([]);
                    setFileName('');
                }, 300);
            }, 1000);

        } catch {
            alert("An error occurred during import.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <AnimatePresence>
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(12px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999
            }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98, y: 10 }}
                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                        background: '#ffffff',
                        width: '100%',
                        maxWidth: '900px',
                        height: '80vh',
                        maxHeight: '700px',
                        borderRadius: '12px',
                        boxShadow: '0 12px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* Header: Progress Bar (HubSpot Style) */}
                    <div style={{ padding: '24px 32px', borderBottom: '1px solid #eaeaea', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111', margin: 0 }}>Import {config.label}</h2>
                        </div>
                        
                        {/* Progress Stepper */}
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: '500px', margin: '0 40px' }}>
                            {steps.map((s, idx) => (
                                <React.Fragment key={s.id}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ 
                                            width: '24px', height: '24px', borderRadius: '50%', 
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '12px', fontWeight: '600',
                                            background: step > s.id ? '#111' : step === s.id ? '#fff' : '#f5f5f5',
                                            border: `2px solid ${step >= s.id ? '#111' : '#eaeaea'}`,
                                            color: step > s.id ? '#fff' : step === s.id ? '#111' : '#999'
                                        }}>
                                            {step > s.id ? <ObliqueIcon name="check" size={14} /> : s.id}
                                        </div>
                                        <span style={{ fontSize: '11px', fontWeight: step >= s.id ? '600' : '500', color: step >= s.id ? '#111' : '#999' }}>{s.label}</span>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div style={{ flex: 1, height: '2px', background: step > s.id ? '#111' : '#eaeaea', margin: '0 8px', transform: 'translateY(-8px)' }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', padding: '4px' }}>
                            <ObliqueIcon name="close" size={20} />
                        </button>
                    </div>

                    {/* Body Area */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '40px 60px' }} className="custom-scrollbar">
                        
                        {/* STEP 1: TYPE */}
                        {step === 1 && (
                            <div className="animate-fade-in" style={{ maxWidth: '800px', margin: '0 auto' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                                    <ImportCard 
                                        img="https://cdn-icons-png.flaticon.com/512/8242/8242502.png" 
                                        title="Import a file" 
                                        desc="One-time import from a file—directly into your Oblique database."
                                        buttonLabel="Import data"
                                        onClick={() => { setImportSource('spreadsheet'); setStep(2); }}
                                    />
                                    <ImportCard 
                                        img="https://cdn-icons-png.flaticon.com/512/3616/3616943.png" 
                                        title="Sync from apps" 
                                        desc="Keep data synced between Oblique and external apps."
                                        buttonLabel="Connect an app"
                                        onClick={() => {}} disabled
                                    />
                                    <ImportCard 
                                        img="https://cdn-icons-png.flaticon.com/512/10006/10006622.png" 
                                        title="Transfer your data" 
                                        desc="Seamlessly transfer your data using the Smart Transfer tool."
                                        buttonLabel="Transfer data"
                                        onClick={() => {}} disabled
                                    />
                                </div>
                            </div>
                        )}

                        {/* STEP 2: UPLOAD */}
                        {step === 2 && (
                            <div className="animate-fade-in" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
                                <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '12px', color: '#111' }}>Upload your files</h3>
                                <p style={{ fontSize: '14px', color: '#666', marginBottom: '32px' }}>
                                    Make sure your file contains headers in the first row. We will map them in the next step.
                                </p>
                                
                                <div 
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ 
                                        border: '1px dashed #ccc', borderRadius: '8px', padding: '60px 20px', 
                                        background: '#fafafa', cursor: 'pointer', transition: 'all 0.2s',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#111'}
                                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#ccc'}
                                >
                                    {isProcessing ? (
                                        <Spinner size="lg" />
                                    ) : (
                                        <>
                                            <ObliqueIcon name="cloud_upload" size={40} style={{ color: '#0052cc' }} />
                                            <div>
                                                <div style={{ fontSize: '15px', fontWeight: '500', color: '#111' }}>Drag and drop or <span style={{ color: '#0052cc' }}>choose a file</span></div>
                                                <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>All .csv and .xlsx files are supported.</div>
                                            </div>
                                        </>
                                    )}
                                    <input 
                                        type="file" accept=".csv, .xlsx" ref={fileInputRef} 
                                        onChange={handleFileUpload} style={{ display: 'none' }} 
                                    />
                                </div>
                            </div>
                        )}

                        {/* STEP 3: MAP */}
                        {step === 3 && (
                            <div className="animate-fade-in">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' }}>
                                    <div>
                                        <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111', marginBottom: '4px' }}>Map columns</h3>
                                        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Select the column headers from your file that match the Oblique database properties.</p>
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#666', background: '#f5f5f5', padding: '6px 12px', borderRadius: '100px' }}>
                                        File: <strong>{fileName}</strong>
                                    </div>
                                </div>
                                
                                <div style={{ border: '1px solid #eaeaea', borderRadius: '8px', overflow: 'hidden' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#fafafa', borderBottom: '1px solid #eaeaea', padding: '12px 20px', fontSize: '12px', fontWeight: '600', color: '#666', textTransform: 'uppercase' }}>
                                        <div>Column Header From File</div>
                                        <div>Oblique Database Property</div>
                                    </div>
                                    
                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }} className="custom-scrollbar">
                                        {sourceHeaders.map(header => {
                                            const mappedKey = Object.keys(fieldMapping).find(k => fieldMapping[k] === header) || '';
                                            const isMapped = mappedKey !== '';
                                            return (
                                                <div key={header} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '16px 20px', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <ObliqueIcon name="data_object" size={16} color="#999" />
                                                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#111' }}>{header}</span>
                                                    </div>
                                                    <div>
                                                        <select
                                                            value={mappedKey}
                                                            onChange={(e) => setFieldMapping(prev => {
                                                                const newMap = { ...prev };
                                                                // Remove old mapping for this header if exists
                                                                Object.keys(newMap).forEach(k => { if (newMap[k] === header) delete newMap[k]; });
                                                                if (e.target.value) newMap[e.target.value] = header;
                                                                return newMap;
                                                            })}
                                                            style={{ 
                                                                width: '100%', padding: '8px 12px', borderRadius: '6px', 
                                                                border: isMapped ? '1px solid #0052cc' : '1px solid #ddd', 
                                                                background: isMapped ? '#eef4ff' : '#fff',
                                                                fontSize: '13px', color: isMapped ? '#0052cc' : '#111', outline: 'none', cursor: 'pointer'
                                                            }}
                                                        >
                                                            <option value="">-- Do not import --</option>
                                                            {config.fields.map(f => (
                                                                <option key={f.key} value={f.key}>{f.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 4: DETAILS */}
                        {step === 4 && (
                            <div className="animate-fade-in" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center', paddingTop: '40px' }}>
                                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#eef4ff', color: '#0052cc', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                                    <ObliqueIcon name="verified" size={40} />
                                </div>
                                <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '12px', color: '#111' }}>Ready to import!</h3>
                                <p style={{ fontSize: '15px', color: '#666', marginBottom: '32px' }}>
                                    We successfully parsed <strong>{rawData.length}</strong> records from <strong>{fileName}</strong>. They will be added to your <strong>{config.label}</strong> database immediately.
                                </p>
                                
                                <div style={{ background: '#fafafa', border: '1px solid #eaeaea', borderRadius: '8px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', textAlign: 'left' }}>
                                    <div>
                                        <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>Data Target</div>
                                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#111' }}>{config.label}</div>
                                    </div>
                                    <div style={{ width: '1px', height: '30px', background: '#eaeaea' }} />
                                    <div>
                                        <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>Rows to Import</div>
                                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#111' }}>{rawData.length} rows</div>
                                    </div>
                                    <div style={{ width: '1px', height: '30px', background: '#eaeaea' }} />
                                    <div>
                                        <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>Columns Mapped</div>
                                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#111' }}>{Object.keys(fieldMapping).length} / {config.fields.length}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Footer Nav */}
                    <div style={{ padding: '20px 32px', borderTop: '1px solid #eaeaea', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
                        <div>
                            {step > 1 && (
                                <button 
                                    onClick={() => setStep(step - 1)} 
                                    style={{ background: '#fff', border: '1px solid #ddd', padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', color: '#111' }}
                                >
                                    Back
                                </button>
                            )}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '14px', fontWeight: '500', cursor: 'pointer', padding: '0 16px' }}>
                                Cancel
                            </button>
                            {step === 3 && (
                                <button 
                                    onClick={() => setStep(4)} 
                                    style={{ background: '#111', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
                                >
                                    Next
                                </button>
                            )}
                            {step === 4 && (
                                <button 
                                    onClick={handleImportExecution} 
                                    disabled={isProcessing}
                                    style={{ 
                                        background: '#0052cc', color: '#fff', border: 'none', padding: '10px 24px', 
                                        borderRadius: '6px', fontSize: '14px', fontWeight: '500', 
                                        cursor: isProcessing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
                                    }}
                                >
                                    {isProcessing ? (
                                        <><Spinner size="sm" color="white" /> Importing...</>
                                    ) : 'Complete Import'}
                                </button>
                            )}
                        </div>
                    </div>

                </motion.div>
            </div>
        </AnimatePresence>
    , document.body);
}

function ImportCard({ img, title, desc, disabled, buttonLabel, onClick }) {
    return (
        <div 
            style={{ 
                border: '1px solid #eaeaea', borderRadius: '8px', padding: '24px', 
                background: disabled ? '#fafafa' : '#fff', opacity: disabled ? 0.6 : 1,
                display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'flex-start',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
            }}
        >
            <div style={{ height: '80px', display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' }}>
                <img src={img} alt={title} style={{ height: '100%', objectFit: 'contain' }} />
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#111', marginBottom: '6px' }}>{title}</div>
                <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>{desc}</div>
            </div>
            
            <button 
                onClick={disabled ? null : onClick}
                style={{
                    marginTop: 'auto', width: '100%',
                    padding: '8px 16px', background: '#fff', border: '1px solid #0052cc', 
                    color: '#0052cc', borderRadius: '4px', fontSize: '13px', fontWeight: '600',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s'
                }}
                onMouseEnter={disabled ? null : e => e.currentTarget.style.background = '#f0f5ff'}
                onMouseLeave={disabled ? null : e => e.currentTarget.style.background = '#fff'}
            >
                {buttonLabel}
            </button>
        </div>
    );
}
